import React, { useState, useContext, useEffect } from 'react';
import { Context as FirstImpressionContext } from '../../../../context/FirstImpressionContext';
import FirstImpressionFileUpload from './FirstImpressionFileUpload';
import FirstImpressionRecordUpload from './FirstImpressionRecordUpload';
import FirstImpressionViewOrRemove from './FirstImpressionViewOrRemove';
import Loader from '../../../common/loader/Loader';
import './FirstImpression.css';

const FirstImpressionSourceSelector = () => {
  const [selectedSource, setSelectedSource] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const {
    state: { firstImpression, firstImpressionStatus, loading },
    fetchFirstImpression,
  } = useContext(FirstImpressionContext);

  useEffect(() => {
    fetchFirstImpression();
  }, [fetchFirstImpression]);

  // Debug: log state changes
  useEffect(() => {
    console.log('[FI Selector] firstImpression:', firstImpression);
  }, [firstImpression]);

  useEffect(() => {
    console.log('[FI Selector] firstImpressionStatus:', firstImpressionStatus);
  }, [firstImpressionStatus]);

  // Check for auto-play demo flag from Dashboard navigation
  useEffect(() => {
    const autoPlayDemo = sessionStorage.getItem('autoPlayDemo');
    if (autoPlayDemo === 'true') {
      setSelectedSource('record');
    }
  }, []);

  const handleSourceSelect = source => {
    setSelectedSource(source);
  };

  const handleBackToSelector = () => {
    setSelectedSource(null);
  };

  // Check if a first impression video already exists
  const hasExistingVideoObject = firstImpression && firstImpression.videoUrl;
  // Fallback: infer existence from status (supports number, array, or object with count)
  const hasExistingVideoByStatus = !!(
    firstImpressionStatus &&
    ((typeof firstImpressionStatus === 'number' && firstImpressionStatus > 0) ||
      (Array.isArray(firstImpressionStatus) &&
        firstImpressionStatus.length > 0) ||
      (typeof firstImpressionStatus === 'object' &&
        firstImpressionStatus.count > 0))
  );
  const hasExistingVideo = hasExistingVideoObject || hasExistingVideoByStatus;

  // Debug: log computed flags
  useEffect(() => {
    console.log(
      '[FI Selector] hasExistingVideoObject:',
      hasExistingVideoObject
    );
    console.log(
      '[FI Selector] hasExistingVideoByStatus:',
      hasExistingVideoByStatus
    );
    console.log('[FI Selector] hasExistingVideo:', hasExistingVideo);
  }, [hasExistingVideoObject, hasExistingVideoByStatus, hasExistingVideo]);

  // Show loading state while fetching data
  if (loading) {
    return <Loader message="Checking your first impression video status..." />;
  }

  // If there's an existing video, show the view/remove component
  if (hasExistingVideo) {
    return <FirstImpressionViewOrRemove />;
  }

  // Render the appropriate component based on selection
  if (selectedSource === 'upload') {
    return (
      <div className="first-impression-container">
        <div className="first-impression-form-header">
          <button
            onClick={handleBackToSelector}
            className="back-button"
            disabled={isUploading}
            style={{
              opacity: isUploading ? 0.5 : 1,
              cursor: isUploading ? 'not-allowed' : 'pointer',
            }}
          >
            ← Back to Options
          </button>
          <div className="first-impression-form-header-icon">📁</div>
          <div className="first-impression-form-header-content">
            <h2>Upload Video File</h2>
          </div>
        </div>
        <FirstImpressionFileUpload onUploadingChange={setIsUploading} />
      </div>
    );
  }

  if (selectedSource === 'record') {
    return (
      <div className="first-impression-container">
        <div className="first-impression-form-header">
          <button
            onClick={handleBackToSelector}
            className="back-button"
            disabled={isUploading}
            style={{
              opacity: isUploading ? 0.5 : 1,
              cursor: isUploading ? 'not-allowed' : 'pointer',
            }}
          >
            ← Back to Options
          </button>
          <div className="first-impression-form-header-icon">🎬</div>
          <div className="first-impression-form-header-content">
            <h2>Record New Video</h2>
          </div>
        </div>
        <FirstImpressionRecordUpload onUploadingChange={setIsUploading} />
      </div>
    );
  }

  // Main selector view
  return (
    <div className="first-impression-container">
      <div className="source-selector">
        <div className="first-impression-form-header">
          <div className="first-impression-form-header-icon">🎥</div>
          <div className="first-impression-form-header-content">
            <h2>Create Your First Impression</h2>
            <p>Choose how you'd like to add your video introduction</p>
          </div>
        </div>

        <div className="source-options" id="first-impression-source-options">
          <div
            className="source-option"
            onClick={() => handleSourceSelect('upload')}
            style={{ transition: 'all 0.3s ease' }}
          >
            <div className="option-icon">📁</div>
            <div className="option-content">
              <h3>Upload Video File</h3>
              <p>Select an existing video file from your device</p>
              <ul className="option-features">
                <li>• Supports MP4, MOV, AVI, and more</li>
                <li>• Quick and easy upload</li>
                <li>• Use videos you've already recorded</li>
              </ul>
            </div>
            <div className="option-arrow">→</div>
          </div>

          <div
            className="source-option"
            onClick={() => handleSourceSelect('record')}
            style={{ transition: 'all 0.3s ease' }}
          >
            <div className="option-icon">🎥</div>
            <div className="option-content">
              <h3>Record New Video</h3>
              <p>Record a video directly using your webcam</p>
              <ul className="option-features">
                <li>• Record with your camera and microphone</li>
                <li>• Preview before uploading</li>
                <li>• Retake if needed</li>
              </ul>
            </div>
            <div className="option-arrow">→</div>
          </div>
        </div>

        <div className="selector-info">
          <div className="info-box">
            <h4>💡 What is a First Impression?</h4>
            <p>
              A First Impression video is a short introduction that helps
              potential employers get to know you better. It's your chance to
              showcase your personality, communication skills, and enthusiasm
              for opportunities.
            </p>
          </div>

          <div className="info-box">
            <h4>📋 Tips for a Great First Impression</h4>
            <ul>
              <li>Keep it brief (30 seconds max)</li>
              <li>Speak clearly and confidently</li>
              <li>Dress professionally</li>
              <li>Choose a quiet, well-lit environment</li>
              <li>Practice your introduction beforehand</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstImpressionSourceSelector;
